// ============================================================================
// lib/checkout/index.ts — API publique unifiée de la couche d'achat.
// ============================================================================
// Depuis un composant paywall, l'ordre du monde est :
//   1. `<PurchaseButton productId="jeraisonne_student_monthly" />`
//   2. Le bouton appelle `purchase(id)` qui route selon la plateforme :
//        - iOS natif → RevenueCat / StoreKit (feuille système Apple)
//        - Android natif → RevenueCat / Google Play Billing
//        - Web → Stripe checkout (redirection existante)
//   3. Le webhook (RC ou Stripe) écrit dans `subscriptions` (source de vérité)
//   4. Le composant invalide les queries React (`student-subscription`,
//      `student-passes`) et l'UI reflète le nouvel accès
//
// Cette abstraction permet aux paywalls d'être STRICTEMENT identiques sur les
// 3 plateformes — la seule différence est le prix affiché (natif = récupéré
// du store, web = catalogue interne).
// ============================================================================

import { isNativeApp } from '@/lib/platform';
import { purchaseNative, restoreNative } from './native';
import { purchaseWeb } from './web';
import type { CheckoutProductId, PurchaseResult } from './types';
import { checkoutMarkOf } from './marks';
import { markCheckoutStarted } from '@/lib/analytics/track';

export type { CheckoutProduct, CheckoutProductId, PurchaseResult } from './types';
export { CHECKOUT_PRODUCTS, getProductById } from './products';
export { initRevenueCat, isRevenueCatReady } from './native';

/** Achète un product par son ID, en routant vers la bonne plateforme. */
export function purchase(productId: CheckoutProductId): Promise<PurchaseResult> {
  markCheckoutStarted(checkoutMarkOf(productId));
  return isNativeApp() ? purchaseNative(productId) : purchaseWeb(productId);
}

/**
 * Restaure les achats précédents (natif seulement). Sur web, le retour du
 * checkout Stripe fait déjà le travail via `?pass_success=1`. Sur natif,
 * indispensable pour la review Apple (Guideline 3.1.1).
 */
export async function restorePurchases(): Promise<PurchaseResult> {
  if (!isNativeApp()) return { status: 'success' }; // no-op web
  return restoreNative();
}
